import { Cloud, GridOn, Info, MoreHoriz, NoDrinks } from '@mui/icons-material';
import { Chip, styled, useMediaQuery, useTheme } from '@mui/material';
import type { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButtonLink } from '~/components/button';
import { hiddenHeader } from '~/components/data-grid';
import { DataSourceKind } from '~/generated/graphql';
import csv from '~/images/csv.svg';
import xlsx from '~/images/xlsx.svg';
import type { DataSourcesList__DataSource as DataSource } from '../../queries/list.generated';
import { Menu } from '../components';

const Logo = styled('img')({ height: '100%', width: '100%' });

export const useColumns = (): GridColDef[] => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));

  return useMemo(
    () => [
      {
        align: 'center',
        field: 'kind',
        ...hiddenHeader(t('common.labels.type')),
        sortable: false,
        renderCell: ({ value: kind }: GridRenderCellParams<DataSource, DataSource['kind']>) => {
          if (kind === DataSourceKind.Integration)
            return <Cloud color="primary" sx={{ width: '100%' }} />;
          return <Logo alt={kind} src={kind === DataSourceKind.Csv ? csv : xlsx} />;
        },
        width: 50,
      },
      {
        field: 'name',
        flex: 1,
        headerName: t('common.labels.name'),
      },
      {
        field: 'internal',
        ...hiddenHeader(t('common.labels.status')),
        minWidth: 150,
        resizable: false,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<DataSource>) =>
          row.includesAlcohol &&
          row.alcoholDisabled && (
            <Chip
              icon={<NoDrinks />}
              label={t('pages.data_sources.list.columns.alcohol_hidden')}
              color="warning"
            />
          ),
      },
      {
        field: 'foreignIdName',
        headerName: t('pages.data_sources.list.columns.unique_id'),
        sortable: false,
        minWidth: 150,
      },
      {
        field: 'keys',
        headerName: t('pages.data_sources.list.columns.keys'),
        sortable: false,
        renderCell: ({ value: keys }: GridRenderCellParams<DataSource, DataSource['keys']>) => {
          return <Chip color="primary" label={keys?.length || 0} />;
        },
        minWidth: 50,
      },
      {
        field: 'items',
        headerName: t('pages.data_sources.list.columns.rows'),
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<DataSource>) => {
          return <Chip color="primary" label={row.items.pageInfo.nodesCount} />;
        },
        minWidth: 50,
      },
      {
        field: 'actions',
        type: 'actions',
        minWidth: isSmallAndUp ? 150 : 0,
        getActions: ({ row: dataSource }: GridRowParams<DataSource>) => {
          return [
            <ActionButtonLink
              key={`details-${dataSource.id}`}
              title={t('common.labels.details')}
              Icon={Info}
              to={`${dataSource.id}`}
            />,
            <ActionButtonLink
              key={`data-${dataSource.id}`}
              title={t('pages.data_sources.tabs.data')}
              Icon={GridOn}
              to={`${dataSource.id}/data`}
            />,
            <Menu
              key={`more-action-${dataSource.id}`}
              dataSource={dataSource}
              Icon={MoreHoriz}
              buttonType="action"
            />,
          ];
        },
      },
    ],
    [isSmallAndUp, t],
  );
};
